// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useParams } from 'react-router-dom';

import {
  fetchInfos,
  selectIsFetchingInfos,
  selectIsLoadedInfos,
} from '@gandi/billing';
import { selectHasDomainCorporateProducts, setCartOwner } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { fetchOneOrganization, setCurrentId } from '@gandi/redux-sharing';
import { localeSelector, useTranslator } from '@gandi/translate';

import type { OwnerType } from 'src/Cart/AccountChoice/components/ResellerOwnerType';
import {
  updateCartProductOwner,
  updateProductsPayloadForOwnership,
} from 'src/Cart/AccountChoice/modules/actions';
import { getSetupOverviewUrl } from 'src/Cart/Setup/modules/helpers';
import { fetchOneCorpoPack } from 'src/Cart/modules/corporate';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { applyTemplateIfNeeded } from 'src/modules/app/actions';
import { selectShopURL } from 'src/modules/config';
import { selectShopCurrent } from 'src/modules/organization/selectors';

type CartSummaryContextType = {
  frozen: boolean,
  orderId: number | null,
  ownerType: OwnerType | null,
  setFrozen: (newValue: boolean) => void,
  setOrderId: (newValue: number) => void,
  setOwnerType: (newValue: OwnerType | null) => void,
  setSubmitting: (newValue: boolean) => void,
  submitting: boolean,
  handleConfigurationClick: (evt: SyntheticEvent<>) => Promise<void>,
  updateSharingIdContext: ({
    newSharingId: string,
    guess?: boolean,
    route?: string,
    reload?: boolean,
  }) => Promise<>,
};

const CartSummaryContext: React.Context<CartSummaryContextType> =
  React.createContext({
    frozen: true,
    orderId: null,
    ownerType: null,
    submitting: false,
    setFrozen: () => {},
    setOrderId: () => {},
    setOwnerType: () => {},
    setSubmitting: () => {},
    updateSharingIdContext: () => Promise.resolve(),
    handleConfigurationClick: () => Promise.resolve(),
  });

export const useCartSummaryContext = (): CartSummaryContextType =>
  React.useContext(CartSummaryContext);

type Props = {
  children: React.MixedElement,
};

export const CartSummaryProvider = ({
  children,
}: Props): React.MixedElement => {
  const dispatch = useDispatch();
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const { __ } = useTranslator();

  const [frozen, setFrozen] = React.useState<boolean>(true);
  const [orderId, setOrderId] = React.useState<number | null>(null);
  const [ownerType, setOwnerType] = React.useState<OwnerType | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const sharingId = currentOrg?.id;

  const billingProps = { sharingId };

  const fetchingBillingInfo = useAppSelector(state =>
    selectIsFetchingInfos(state, billingProps),
  );

  const loadedBillingInfo = useAppSelector(state =>
    selectIsLoadedInfos(state, billingProps),
  );

  const corporateProduct = useAppSelector(selectHasDomainCorporateProducts);

  const locale = useAppSelector(localeSelector);
  const shop = useAppSelector(selectShopURL);

  const updateSharingIdContext: (opts: {
    newSharingId: string,
    guess?: boolean,
    route?: string,
    reload?: boolean,
  }) => Promise<void> = React.useCallback(
    async ({
      newSharingId,
      guess = false,
      route: route_ = '',
      reload = false,
    }) => {
      setFrozen(true);
      // FIXME: once sufficiently validated, propagate this change
      // to other cases of redirectToSharingIdContext.
      try {
        if (reload) {
          throw new Error('Perform a hard-reload.');
        }
        // Fetch the current org's details.
        await dispatch(fetchOneOrganization(newSharingId));

        // Override the current id in the redux state.
        dispatch(setCurrentId(newSharingId));

        // This will be reverted to the inital sharing id
        // if user leaves this page, as a mitigation of the sharing_id in the URL
        // dictating the context.
        // See `Configure`'s `componenWillUnmount` lifecycle method.

        // Alway fetch the current org's prepaid info
        // Necessary to:
        // - ensure current cart owner has a currency set
        // - check owner's grid against bundle grid limitations if any.

        const promises = [];
        if (!fetchingBillingInfo && !loadedBillingInfo) {
          promises.push(dispatch(fetchInfos()));
        }

        if (currentOrg.corporate && corporateProduct) {
          promises.push(dispatch(fetchOneCorpoPack(newSharingId)));
        }

        await Promise.allSettled(promises);

        setFrozen(false);

        const route = route_;
        if (route) {
          navigate(route);
        }
      } catch (err) {
        // Pass and do hard-reload redirect.
        const route = route_ || `/cart/${cartId}/configure/account-choice`;
        let redirect = `${shop}/${locale}/${newSharingId}${route}`;
        if (guess) {
          redirect = `${redirect}?guess`;
        }
        global.window.location = redirect;
      }
    },
    [
      cartId,
      corporateProduct,
      currentOrg?.corporate,
      dispatch,
      fetchingBillingInfo,
      loadedBillingInfo,
      locale,
      navigate,
      setFrozen,
      shop,
    ],
  );

  const handleConfigurationClick: (evt: SyntheticEvent<>) => Promise<void> =
    React.useCallback(
      async (evt: SyntheticEvent<>) => {
        evt.preventDefault();

        setFrozen(true);
        setSubmitting(true);

        try {
          await dispatch(applyTemplateIfNeeded());
          await dispatch(updateCartProductOwner());
          await dispatch(setCartOwner({}));
          await dispatch(updateProductsPayloadForOwnership());

          // Perform a hard-reload only if the cart owner / sharing_id context
          // has been updated.
          const reload = !global.window.location.href.includes(sharingId);
          await updateSharingIdContext({
            newSharingId: sharingId,
            guess: false,
            route: getSetupOverviewUrl(cartId),
            reload,
          });
        } catch (err) {
          setFrozen(false);
          setSubmitting(false);
          dispatch(notifyDanger(__('Unexpected error occurred')));
        }
      },
      [__, cartId, dispatch, sharingId, updateSharingIdContext],
    );

  const handleSetFrozen = React.useCallback(
    (newValue: boolean) => setFrozen(newValue),
    [],
  );

  const handleSetOrderId = React.useCallback(
    (newValue: number) =>
      setOrderId(newValue ? parseInt(newValue, 10) : newValue),
    [],
  );

  const handleSetOwnerType = React.useCallback(
    (newValue: OwnerType | null) => setOwnerType(newValue),
    [],
  );
  const handleSetSubmitting = React.useCallback(
    (newValue: boolean) => setSubmitting(newValue),
    [],
  );

  const contextValue = React.useMemo(
    () => ({
      frozen,
      orderId,
      ownerType,
      submitting,
      setFrozen: handleSetFrozen,
      setOrderId: handleSetOrderId,
      setOwnerType: handleSetOwnerType,
      setSubmitting: handleSetSubmitting,
      handleConfigurationClick,
      updateSharingIdContext,
    }),
    [
      frozen,
      orderId,
      handleSetFrozen,
      handleSetOrderId,
      handleSetOwnerType,
      handleSetSubmitting,
      ownerType,
      submitting,
      handleConfigurationClick,
      updateSharingIdContext,
    ],
  );

  return (
    <CartSummaryContext.Provider value={contextValue}>
      {children}
    </CartSummaryContext.Provider>
  );
};
